import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDarkMode } from "@/hooks/useDarkMode";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Logo } from "@/components/Logo";
import { Loader2, Moon, Bell, Fingerprint, LogOut, KeyRound, Globe } from "lucide-react";

export default function AgentProfile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useDarkMode();
  const [profile, setProfile] = useState(null);
  const [notif, setNotif] = useState(true);
  const [bio, setBio] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/agent/profile").then((r) => setProfile(r.data));
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const changePw = async () => {
    setSaving(true);
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password changed");
      setPwOpen(false);
      setPw({ current_password: "", new_password: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <h1 className="font-display text-2xl font-bold tracking-tight">Profile & settings</h1>

      <Card className="p-5">
        <p className="font-display text-lg font-bold">{profile.name}</p>
        <p className="text-sm text-muted-foreground">{profile.email}</p>
        <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
          <Info label="Agent ID" value={profile.agent_code} />
          <Info label="Country" value={profile.country} />
          <Info label="Currency" value={profile.currency} />
          <Info label="Time zone" value={profile.timezone} />
        </div>
      </Card>

      <Card className="divide-y divide-border">
        <Toggle icon={Moon} label="Dark mode" checked={dark} onChange={setDark} testid="toggle-dark" />
        <Toggle icon={Bell} label="Notifications" checked={notif} onChange={setNotif} testid="toggle-notifications" />
        <Toggle icon={Fingerprint} label="Biometric unlock (Face ID)" checked={bio} onChange={setBio} testid="toggle-biometric" />
        <Row icon={Globe} label="Language" value="English" />
      </Card>

      <PushCard />

      <Card className="p-4 flex items-start gap-3 text-sm" data-testid="password-policy-note">
        <KeyRound className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
        <p className="text-muted-foreground">Your login password is issued by the office. If you forget it or it is locked, contact your administrator for a new one.</p>
      </Card>

      <Button variant="outline" className="w-full h-12 justify-start text-destructive" onClick={doLogout} data-testid="agent-logout-button">
        <LogOut className="w-4 h-4 mr-2" /> Logout
      </Button>

      <div className="flex flex-col items-center gap-2 pt-4 opacity-60">
        <Logo size={22} />
        <p className="text-[11px] text-muted-foreground">App version 1.0.0</p>
      </div>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Current password</Label>
              <Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="current-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">New password</Label>
              <Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="new-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={changePw} disabled={saving} data-testid="submit-change-password">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-medium">{value}</p>
    </div>
  );
}
function Toggle({ icon: Icon, label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between p-4">
      <span className="flex items-center gap-3 text-sm"><Icon className="w-4 h-4 text-muted-foreground" /> {label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between p-4">
      <span className="flex items-center gap-3 text-sm"><Icon className="w-4 h-4 text-muted-foreground" /> {label}</span>
      <span className="text-sm text-muted-foreground">{value}</span>
    </div>
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

function PushCard() {
  const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  const [enabled, setEnabled] = useState(false);
  const [devices, setDevices] = useState(0);
  const [busy, setBusy] = useState(false);
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/agent/push/status");
      setDevices(data.devices);
      if (supported) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setEnabled(!!sub && Notification.permission === "granted");
      }
    } catch (e) {
      console.warn("Push status unavailable:", e?.message);
    }
  }, [supported]);
  useEffect(() => { refresh(); }, [refresh]);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notifications were not allowed on this device"); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/agent/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(data.public_key) });
      await api.post("/agent/push/subscribe", { subscription: sub.toJSON(), user_agent: navigator.userAgent });
      toast.success("Push notifications enabled on this device");
      await api.post("/agent/push/test");
      refresh();
    } catch (e) {
      toast.error("Could not enable push: " + (e.message || "unknown error"));
    } finally {
      setBusy(false);
    }
  };
  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await api.post("/agent/push/unsubscribe", { endpoint: sub.endpoint }); await sub.unsubscribe(); }
      toast.success("Push notifications disabled on this device");
      refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4 text-sm" data-testid="push-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-medium">Phone notifications</p>
          <p className="text-xs text-muted-foreground">{supported ? `Payouts, deposit changes, approvals and messages as push alerts. ${devices} device(s) enrolled.` : "Not supported in this browser. On iPhone, add the app to your Home Screen first (iOS 16.4+)."}</p>
        </div>
        {supported && (enabled
          ? <Button size="sm" variant="outline" disabled={busy} onClick={disable} data-testid="push-disable-button">Disable</Button>
          : <Button size="sm" disabled={busy} onClick={enable} data-testid="push-enable-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enable"}</Button>)}
      </div>
    </Card>
  );
}
